import moment from 'moment';
import { Badge, Button } from 'reactstrap';
import { AnnualReviewStatus } from '../../../AnnualReview/types';
import { Link } from 'react-router-dom';

interface AnnualReviewPendingColumnsOptions {
  onOpen: (row: any) => void;
  onCancel: (e: React.FormEvent, row: any) => void;
}

type ActorStatus = 'pending' | 'draft' | 'completed' | 'awaiting_employee';

const actorStatusFor = (completedAt: string | null | undefined, answers: any): ActorStatus => {
  if (completedAt) return 'completed';
  if (answers && (Object.keys(answers?.ratings || {}).length || answers?.directorReview || answers?.selfReflection)) {
    return 'draft';
  }
  return 'pending';
};

const supervisorStatusFor = (row: any): ActorStatus => {
  if (!row?.employeeCompletedAt) return 'awaiting_employee';
  return actorStatusFor(row?.supervisorCompletedAt, row?.supervisorAnswers);
};

type PayRateStatus = 'not_started' | 'skipped' | 'waiting_finance' | 'approved' | 'declined';

const payRateStatusFor = (row: any): PayRateStatus => {
  const decision = row?.proposedPayRate?.decision;
  if (decision?.outcome === 'approved') return 'approved';
  if (decision?.outcome === 'denied') return 'declined';
  if (row?.createdPayRateId) return 'waiting_finance';
  if (row?.proposedPayRate && row?.proposedPayRate?.increasePercent === 0) return 'skipped';
  return 'not_started';
};

const actionLabelFor = (status: AnnualReviewStatus): { title: string; icon: string; color: string } => {
  if (status === 'AWAITING_GROWTH_PLAN') {
    return { title: 'Continue Annual Review', icon: 'fa-clipboard-list', color: 'warning' };
  }
  if (status === 'AWAITING_PAY_RAISE_DECISION') {
    return {
      title: 'Waiting Finance — open to check status or update Growth Plan',
      icon: 'fa-hourglass-half',
      color: 'info',
    };
  }
  if (status === 'AWAITING_SIGNATURES') {
    return { title: 'Upload Signed PDF', icon: 'fa-file-signature', color: 'warning' };
  }
  return { title: 'Fill My Section', icon: 'fa-edit', color: 'success' };
};

const StatusBadge = ({ status }: { status: ActorStatus }) => {
  const config: Record<ActorStatus, { color: string; icon: string; label: string }> = {
    completed: { color: 'success', icon: 'fa-check', label: 'Completed' },
    draft: { color: 'info', icon: 'fa-pen-to-square', label: 'Draft' },
    pending: { color: 'warning', icon: 'fa-clock', label: 'Pending' },
    awaiting_employee: { color: 'secondary', icon: 'fa-lock', label: 'Awaiting employee' },
  };
  const c = config[status];
  return (
    <Badge color={c.color} pill style={{ fontSize: '0.75rem' }}>
      <i className={`fas ${c.icon} mr-1`} />
      {c.label}
    </Badge>
  );
};

const PayRateStatusBadge = ({ status }: { status: PayRateStatus }) => {
  const config: Record<PayRateStatus, { color: string; icon: string; label: string }> = {
    approved: { color: 'success', icon: 'fa-check', label: 'Approved' },
    declined: { color: 'danger', icon: 'fa-xmark', label: 'Declined' },
    waiting_finance: { color: 'info', icon: 'fa-hourglass-half', label: 'Waiting Finance' },
    skipped: { color: 'secondary', icon: 'fa-forward', label: 'Skipped' },
    not_started: { color: 'secondary', icon: 'fa-minus', label: 'Not started' },
  };
  const c = config[status];
  return (
    <Badge color={c.color} pill style={{ fontSize: '0.75rem' }}>
      <i className={`fas ${c.icon} mr-1`} />
      {c.label}
    </Badge>
  );
};

export const getAnnualReviewPendingColumns = (options: AnnualReviewPendingColumnsOptions) => {
  return [
    {
      name: 'Name',
      selector: (row: any) => row?.employeeName || '-',
      sortable: true,
      grow: 2,
      minWidth: '220px',
    },
    {
      name: 'Start Date',
      selector: (row: any) => row?.employeeStartDate,
      sortable: true,
      format: (row: any) => (row.employeeStartDate ? moment.utc(row.employeeStartDate).format('MM/DD/YYYY') : '-'),
      width: '140px',
    },
    {
      name: 'Employee Status',
      cell: (row: any) => <StatusBadge status={actorStatusFor(row?.employeeCompletedAt, row?.employeeAnswers)} />,
      width: '180px',
    },
    {
      name: 'Supervisor Status',
      cell: (row: any) => <StatusBadge status={supervisorStatusFor(row)} />,
      width: '180px',
    },
    {
      name: 'Pay Rate Status',
      cell: (row: any) => <PayRateStatusBadge status={payRateStatusFor(row)} />,
      width: '180px',
    },
    {
      name: 'Actions',
      cell: (row: any) => {
        const action = actionLabelFor(row.status as AnnualReviewStatus);
        return (
          <div className="d-flex justify-content-center">
            <Button
              outline
              color={action.color}
              size="sm"
              style={{ padding: '0.1rem 0.4rem', border: 'none' }}
              title={action.title}
              tag={Link}
              to={`/staff/annual-reviews/${row.id}`}
              onClick={() => options.onOpen(row)}
            >
              <i className={`fas ${action.icon}`} />
            </Button>
            <Button
              outline
              color="danger"
              size="sm"
              style={{ padding: '0.1rem 0.4rem', border: 'none' }}
              title="Cancel Annual Review"
              onClick={(e: React.FormEvent) => options.onCancel(e, row)}
            >
              <i className="fas fa-trash" />
            </Button>
          </div>
        );
      },
      center: true,
      width: '120px',
    },
  ];
};
